import { useEffect } from "react";
import { Link } from "react-router-dom";
import CustomHero from "./components/CustomHero";
import CustomFeatures from "./components/CustomFeatures";
import CustomProcess from "./components/CustomProcess";
import CustomOptions from "./components/CustomOptions";
import CustomCTA from "./components/CustomCTA";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const CustomItineraryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "自訂行程 | Chillgo · 全站最熱門 · 香港自駕遊專家";

    // JSON-LD Schema for custom itinerary service page
    const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "https://example.com";
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.id = "custom-itinerary-jsonld";
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Chillgo 全自訂自駕遊行程服務",
      serviceType: "自訂旅遊行程規劃",
      description: "由香港出發，100% 自訂路線、天數、住宿與節奏。Chillgo 顧問免費設計方案，無限次修改，最快 3 天出發。川藏線、新疆、歐洲等頂級自駕路線全覆蓋。",
      url: `${siteUrl}/custom-itinerary`,
      provider: {
        "@type": "TravelAgency",
        name: "Chillgo",
        url: siteUrl,
        telephone: "+852-5348-2337",
        email: "chillgoooo@gmail.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "彌敦道 688號 18樓 1801室",
          addressLocality: "旺角",
          addressRegion: "九龍",
          addressCountry: "HK",
        },
        sameAs: [
          "https://www.facebook.com/profile.php?id=61582054874473",
          "https://www.instagram.com/chill_goooo",
        ],
      },
      areaServed: {
        "@type": "Place",
        name: "香港及大中華地區",
      },
      offers: {
        "@type": "Offer",
        description: "免費諮詢及行程設計，無需付訂金，滿意才確認",
        priceCurrency: "HKD",
        availability: "https://schema.org/InStock",
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首頁", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "自訂行程", item: `${siteUrl}/custom-itinerary` },
        ],
      },
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existing = document.getElementById("custom-itinerary-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-100">
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
                alt="Chill Go Logo"
                className="h-12 w-auto object-contain"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-6">
              {[
                { label: "首頁", to: "/" },
                { label: "目的地", to: "/#destinations" },
                { label: "自訂行程", to: "/custom-itinerary", active: true },
                { label: "報價說明", to: "/#pricing" },
                { label: "聯繫我們", to: "/#contact" },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    item.active
                      ? "text-orange-500"
                      : "text-gray-600 hover:text-orange-500"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <a
              href="https://wa.me/85253482337?text=%E4%BD%A0%E5%A5%BD%EF%BC%8C%E6%88%91%E6%83%B3%E6%9F%A5%E8%A9%A2%E8%87%AA%E8%A8%82%E8%A1%8C%E7%A8%8B"
              target="_blank"
              rel="nofollow noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-1.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
              <span>立即查詢</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Page Sections */}
      <div className="pt-16">
        <CustomHero />
        <CustomFeatures />
        <CustomProcess />
        <CustomOptions />
        <CustomCTA />
      </div>

      {/* Footer */}
      <footer className="bg-stone-800 text-white py-10">
        <div className="w-full px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img
                src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
                alt="Chill Go Logo"
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-stone-400 text-sm">© {new Date().getFullYear()} Chill Go Limited. 版權所有</p>
            <Link to="/" className="text-stone-400 hover:text-orange-400 text-sm transition-colors cursor-pointer">
              ← 返回主頁
            </Link>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
};

export default CustomItineraryPage;
